import { createAsyncThunk } from "@reduxjs/toolkit"

import { type RootState } from "../../../store.ts"
import { handleGetResponseData } from "../../../utils/handlers/handle-get-response-data"
import * as services from "../services"
import {
  clearTabPrefs,
  setAllPrefs,
  setError,
  setLoading,
  replaceSurfacePrefs,
  setPrefsLoaded,
  setSurfacePref,
  setTablePrefs
} from "./ui-preferences.slice"
import type {
  ColumnConfigItem,
  SurfaceKey,
  TabKey,
  TableConfig
} from "./ui-preferences.types"

const errorMessage = (e: unknown, fallback: string): string =>
  typeof e === "string" ? e : ((e as { message?: string })?.message ?? fallback)

type PatchArgs = {
  tableKey: string
  tabKey: TabKey
  columns: ColumnConfigItem[]
}

export const patchTablePrefs = createAsyncThunk<
  void,
  PatchArgs,
  { state: RootState }
>("userUIPreferences/patchTablePrefs", async (args, { dispatch, getState }) => {
  const before = getState().userUIPreferences.prefs
  dispatch(setTablePrefs(args))
  dispatch(setLoading(true))
  dispatch(setError(null))
  try {
    const currentTables = getState().userUIPreferences.prefs.tables ?? {}
    const updatedTables: Record<string, TableConfig> = {
      ...currentTables,
      [args.tableKey]: {
        ...(currentTables[args.tableKey] ?? {}),
        [args.tabKey]: { columns: args.columns }
      }
    }
    const responseData = await services.patchUIPreferences({
      list_config: { tables: updatedTables }
    })
    const response = handleGetResponseData(responseData)
    if (!response.ok) throw response.data ?? new Error("Failed to save layout")
    if (response.data) {
      dispatch(setAllPrefs({ tables: response.data.list_config?.tables ?? {} }))
    }
  } catch (e: unknown) {
    dispatch(setAllPrefs(before))
    dispatch(setError(errorMessage(e, "Could not save layout")))
  } finally {
    dispatch(setLoading(false))
  }
})

type ResetArgs = { tableKey: string; tabKey: TabKey }

export const resetTabPrefs = createAsyncThunk<
  void,
  ResetArgs,
  { state: RootState }
>("userUIPreferences/resetTabPrefs", async (args, { dispatch, getState }) => {
  const before = getState().userUIPreferences.prefs
  dispatch(clearTabPrefs(args))
  dispatch(setLoading(true))
  dispatch(setError(null))
  try {
    const currentTables = getState().userUIPreferences.prefs.tables ?? {}
    const updatedTables: Record<string, TableConfig> = { ...currentTables }
    if (updatedTables[args.tableKey]) {
      const tabCopy = { ...updatedTables[args.tableKey] }
      delete tabCopy[args.tabKey]
      updatedTables[args.tableKey] = tabCopy
    }
    const responseData = await services.patchUIPreferences({
      list_config: { tables: updatedTables }
    })
    const response = handleGetResponseData(responseData)
    if (!response.ok) throw response.data ?? new Error("Failed to reset layout")
    if (response.data) {
      dispatch(setAllPrefs({ tables: response.data.list_config?.tables ?? {} }))
    }
  } catch (e: unknown) {
    dispatch(setAllPrefs(before))
    dispatch(setError(errorMessage(e, "Could not reset layout")))
  } finally {
    dispatch(setLoading(false))
  }
})

export const loadUIPreferences = createAsyncThunk<void, void>(
  "userUIPreferences/load",
  async (_, { dispatch }) => {
    try {
      const responseData = await services.getUIPreferences()
      const response = handleGetResponseData(responseData)
      if (response.ok && response.data) {
        dispatch(setAllPrefs({ tables: response.data.list_config?.tables ?? {} }))
        dispatch(replaceSurfacePrefs(response.data.preferences ?? {}))
      }
    } finally {
      // Marked loaded even when the read failed, so a surface waiting on it falls
      // through to its own default instead of hanging.
      dispatch(setPrefsLoaded())
    }
  }
)

type SurfacePreferenceArgs = { key: SurfaceKey; value: unknown }

// Newest write per key: a slow failure must not roll back a fresher choice.
const latestWrite = new Map<SurfaceKey, symbol>()

export const storeSurfacePreference = createAsyncThunk<
  void,
  SurfacePreferenceArgs,
  { state: RootState }
>(
  "userUIPreferences/storeSurfacePreference",
  async ({ key, value }, { dispatch, getState }) => {
    // Optimistic: a caller that navigates on the same click must read the new
    // value, not race the round trip.
    const before = getState().userUIPreferences.surfacePrefs?.[key] ?? null
    const write = Symbol(key)
    latestWrite.set(key, write)
    dispatch(setSurfacePref({ key, value }))
    try {
      const responseData = await services.patchUIPreferences({
        preferences: { [key]: value }
      })
      const response = handleGetResponseData(responseData)
      if (!response.ok) throw response.data ?? new Error("Failed to save preference")
    } catch {
      // One key, not the whole bag: another surface may have saved its own while
      // this request was out, and the server kept it. Silent by design too — a
      // remembered view is not worth an error banner, and `error` drives the
      // table-layout alert on an unrelated screen.
      if (latestWrite.get(key) === write) {
        dispatch(setSurfacePref({ key, value: before }))
      }
    }
  }
)
